import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import { Button, Empty, Flex, Form, message, Skeleton, Space, Tag, Typography } from 'antd';
import {
  BankOutlined,
  CrownOutlined,
  LeftOutlined,
  ProjectOutlined,
  RightOutlined,
  StarFilled,
  UserDeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';

import Drawer from '@/components/Drawer';
import InfoCard from '@/components/ProjectDetails/Cards/InfoCard';
import ContactFields, {
  ContactFieldName,
  ContactFieldValues,
} from '@/components/Contacts/ContactFields';
import ContactAvatar from '@/components/Contacts/ContactAvatar';
import ActionsMenu, { ActionsMenuItem } from '@/components/Contacts/ActionsMenu';
import PhoneNumberFormat from '@/components/format/PhoneNumberFormat';
import {
  deleteCustomerContactRequest,
  setCustomerPrimaryContactRequest,
  updateCustomerContactRequest,
} from '@/services/api/contact';
import * as LeadSelectors from '@/selectors/leadSelectors';
import { createCustomerRequest } from '@/services/api/customer';
import * as ContactSelectors from '@/selectors/contactSelectors';
import { ContactCustomerSummary, ContactModel, CUSTOMER_TYPES } from '@/typings/models/Contact';
import { LeadModel } from '@/typings/models/Lead';
import PrettyDate, { FormatDate } from '@/components/PrettyDate';
import { getLeadTitle } from '@/utils/lead';
import PERMISSIONS from '@/constants/permissions';
import { useCustomerRequiredFields, useHasAccess } from '@/utils/hooks';
import { processAPIResponseNow } from '@/utils/request';
import { applyServerErrorsToForm, normalizeApiError } from '@/utils/apiErrors';
import { F, getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import {
  CONTACT_ENTITY,
  getContactCustomer,
  getContactCustomerId,
  getContactCustomers,
  getContactName,
  getCustomerLabel,
} from '@/utils/contacts';
import { usePartnerDuplicateContact } from '@/components/Contacts/usePartnerDuplicateContact';
import DuplicateElsewhereNotice from '@/components/Contacts/DuplicateElsewhereNotice';
import { useContactRoleSuggestions } from '@/components/Contacts/useContactRoleSuggestions';
import { useContactProjects } from './useContactProjects';
import styles from './index.module.less';

const EMPTY_VALUES: ContactFieldValues = {};
/** Customers named in the header before the rest folds into "+n". */
const MAX_HEADER_CUSTOMERS = 2;
const CREATE_FIELDS = ['firstname', 'lastname', 'email', 'phone', 'gender', 'locale', 'role'] as const;
/** Partner-required customer fields the contact form shows. */
const REQUIRABLE_FIELDS: ContactFieldName[] = ['email', 'phone', 'locale', 'gender'];

/** One of the contact's projects: title, status and last change; marked when the contact leads it. */
const ContactProjectRow: React.FC<{
  lead: LeadModel;
  contact: ContactModel;
  onOpen: (leadId: string) => void;
}> = ({ lead, contact, onOpen }) => {
  const title = getLeadTitle(lead).trim();
  const state = lead.lead_state;
  return (
    <button type="button" className={styles.projectRow} onClick={() => lead.id && onOpen(lead.id)}>
      <span className={styles.projectText}>
        <span className={styles.projectTitle}>
          {title && title !== '-' ? title : <FormatDate date={lead.created} format={'YYYY-MM-DD'} />}
        </span>
        <span className={styles.projectMeta}>
          {state && (
            <span className={styles.projectState}>
              {/* The status colour is data, so it's set inline. */}
              <span className={styles.projectStateDot} style={{ background: state.color }} />
              {state.label}
            </span>
          )}
          {lead.modified && <PrettyDate date={lead.modified} />}
        </span>
      </span>
      {lead.primary_contact_id === contact.id && (
        <span className={styles.projectPrimary}>
          <StarFilled /> <F id="component.contacts.primary" defaultMessage="Primary contact" />
        </span>
      )}
      <RightOutlined className={styles.projectChevron} />
    </button>
  );
};

const ContactDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const canEdit = useHasAccess([PERMISSIONS.Customers.EDIT]);

  const drawer = useSelector(ContactSelectors.selectContactDrawer);
  const loading = useSelector(ContactSelectors.selectContactDetailsLoading);
  const contact: ContactModel | undefined = useSelector((state: DefaultRootState) =>
    ContactSelectors.selectContactById(state, drawer.contactId),
  );

  const isCreate = drawer.visible && !drawer.contactId;
  // A contact can belong to several customers: it's read and edited through the one it was opened for.
  const customerId = drawer.customerId || getContactCustomerId(contact);
  // Opened from a project: its lead knows the customer when the contact row doesn't.
  const lead: LeadModel | undefined = useSelector((state: DefaultRootState) =>
    LeadSelectors.selectLeadData(state, drawer.leadId),
  );
  const customer: Partial<ContactCustomerSummary> | undefined =
    (lead?.customer?.id === customerId ? lead?.customer : undefined) || getContactCustomer(contact, customerId);
  // The customer in context first, then the contact's other customers (as far as the list row knows them).
  const customers = useMemo(() => {
    const others = getContactCustomers(contact).filter((link) => link.id !== customer?.id);
    return customer ? [customer, ...others] : others;
  }, [contact, customer]);
  const isOrganization = customer?.type === CUSTOMER_TYPES.ORGANIZATION;
  const partnerRequiredFields = useCustomerRequiredFields();
  const partnerRequired = REQUIRABLE_FIELDS.filter((field) => partnerRequiredFields.includes(field));
  // A new contact here is a new person customer; a person customer's primary
  // contact is that customer. Both follow the partner's customer rules.
  const requiredFields: ContactFieldName[] =
    isCreate || (contact?.is_primary && !isOrganization) ? ['firstname', 'lastname', ...partnerRequired] : [];
  const projects = useContactProjects(isCreate ? null : contact);
  const roleSuggestions = useContactRoleSuggestions();
  const createEmail = Form.useWatch('email', form);
  const createPhone = Form.useWatch('phone', form);
  // `POST /customers` doesn't deduplicate: point at the person who already has this email/phone.
  const duplicate = usePartnerDuplicateContact(isCreate ? { email: createEmail, phone: createPhone } : {});

  useEffect(() => {
    if (drawer.visible && drawer.contactId) {
      dispatch({
        type: 'contacts/fetchDetails',
        payload: { contactId: drawer.contactId, customerId: drawer.customerId },
      });
    }
  }, [drawer.visible, drawer.contactId, drawer.toggledAt]);

  useEffect(() => {
    if (isCreate) form.resetFields();
  }, [isCreate, drawer.toggledAt]);

  const close = useCallback(() => dispatch({ type: 'contacts/closeDrawer' }), [dispatch]);

  const values: ContactFieldValues = useMemo(
    () =>
      contact
        ? {
            firstname: contact.firstname,
            lastname: contact.lastname,
            email: contact.email,
            phone: contact.phone,
            gender: contact.gender,
            locale: contact.locale,
            role: contact.role,
          }
        : EMPTY_VALUES,
    [contact],
  );

  // Field errors land on their fields; anything else is a toast.
  const showServerErrors = useCallback(
    (data: unknown) => {
      const { fieldErrors, message: text } = normalizeApiError(data);
      if (Object.keys(fieldErrors).length) applyServerErrorsToForm(form, fieldErrors);
      else message.error(text);
    },
    [form],
  );

  const handleFieldSave = useCallback(
    async (field: ContactFieldName, value: string | null) => {
      if (!contact || !customerId) return;
      const response = await updateCustomerContactRequest({
        params: { customerId, contactId: contact.id },
        data: { [field]: value },
      });
      processAPIResponseNow(
        response,
        {
          // Inline edits save silently, as on the project card; errors still show.
          onSuccess: (saved: ContactModel) => {
            const merged = { ...contact, ...saved, customer_id: customerId };
            dispatch({ type: 'contacts/saveDetails', payload: merged });
            // Keep every open project showing this contact in step.
            const leadIds = new Set([drawer.leadId, ...(projects || []).map((lead) => lead.id)]);
            leadIds.forEach(
              (leadId) => leadId && dispatch({ type: 'leads/updateLeadContact', payload: { leadId, contact: saved } }),
            );
          },
          onError: (data) => showServerErrors(data),
        },
        'data',
      );
    },
    [contact, customerId, dispatch, drawer.leadId, form, projects],
  );

  const handleCreate = async () => {
    let fields: Record<string, string | undefined>;
    try {
      fields = await form.validateFields([...CREATE_FIELDS]);
    } catch {
      return;
    }
    setSaving(true);
    const response = await createCustomerRequest({
      data: {
        type: 'person',
        ...Object.fromEntries(CREATE_FIELDS.map((field) => [field, fields[field]?.trim() || null])),
      },
    });
    setSaving(false);
    processAPIResponseNow(response, {
      successMessage: getIntlTranslation(intl, {
        entity: CONTACT_ENTITY,
        action: INTL_ACTION.CREATE_SUCCESS,
      }),
      onSuccess: () => {
        close();
        dispatch({ type: 'contacts/fetchList' });
      },
      onError: (data) => showServerErrors(data),
    });
  };

  // The backend moves the contact's projects to other contacts, so the open
  // project and the list are read again rather than patched here.
  const refreshAfterCustomerChange = useCallback(() => {
    dispatch({ type: 'contacts/fetchList' });
    if (drawer.leadId) dispatch({ type: 'leads/fetchSingleLead', payload: { leadId: drawer.leadId } });
  }, [dispatch, drawer.leadId]);

  /** Unlinks from this customer: off all its projects; the backend deletes a contact left without customers. */
  const handleRemove = async () => {
    if (!contact || !customerId) return;
    setSaving(true);
    const response = await deleteCustomerContactRequest({ params: { customerId, contactId: contact.id } });
    setSaving(false);
    processAPIResponseNow(response, {
      successMessage: getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.REMOVE_SUCCESS }),
      onSuccess: () => {
        close();
        refreshAfterCustomerChange();
      },
      onError: (data) => showServerErrors(data),
    });
  };

  const handleMakeCustomerPrimary = async () => {
    if (!contact || !customerId) return;
    setSaving(true);
    const response = await setCustomerPrimaryContactRequest({ params: { customerId }, contactId: contact.id });
    setSaving(false);
    processAPIResponseNow(response, {
      onSuccess: () => {
        dispatch({ type: 'contacts/fetchDetails', payload: { contactId: contact.id, customerId } });
        refreshAfterCustomerChange();
      },
      onError: (data) => showServerErrors(data),
    });
  };

  const openProject = useCallback(
    (leadId: string) => dispatch({ type: 'leads/toggleLeadDetailsDrawer', payload: { lead: { id: leadId } } }),
    [dispatch],
  );

  const title = (
    <Space size={16}>
      <Button icon={<LeftOutlined />} onClick={close} className={styles.backButton} />
      {isCreate ? (
        <F entity={CONTACT_ENTITY} action={INTL_ACTION.NEW} />
      ) : (
        <F entity={CONTACT_ENTITY} action={INTL_ACTION.DETAILS} />
      )}
    </Space>
  );

  const createFooter = (
    <Flex justify="end" gap={8}>
      <Button onClick={close} disabled={saving}>
        <F id="common.actions.cancel" defaultMessage="Cancel" />
      </Button>
      <Button type="primary" onClick={handleCreate} loading={saving}>
        <F id="common.actions.save" defaultMessage="Save" />
      </Button>
    </Flex>
  );

  // Customer-level actions sit in the header's ⋯ menu; both reach beyond this
  // contact, so both ask first.
  const actionItems: ActionsMenuItem[] =
    contact && canEdit && customerId
      ? [
          ...(contact.is_primary
            ? []
            : [
                {
                  key: 'customerPrimary',
                  icon: <CrownOutlined />,
                  label: intl.formatMessage({
                    id: 'component.contacts.makeCustomerPrimary',
                    defaultMessage: "Make the customer's primary contact",
                  }),
                  confirm: {
                    title: intl.formatMessage({
                      id: 'component.contacts.makeCustomerPrimary',
                      defaultMessage: "Make the customer's primary contact",
                    }),
                    content: (
                      <F
                        id="component.contacts.makeCustomerPrimaryHint"
                        defaultMessage="{type, select, organization {Customer emails then go to this contact in all of the customer's projects.} other {The customer then takes this contact's name, email and phone in all of its projects.}}"
                        values={{ type: customer?.type || CUSTOMER_TYPES.PERSON }}
                      />
                    ),
                  },
                  onSelect: handleMakeCustomerPrimary,
                },
              ]),
          {
            key: 'remove',
            icon: <UserDeleteOutlined />,
            label: intl.formatMessage({ id: 'component.contacts.removeFromCustomer', defaultMessage: 'Remove from customer' }),
            danger: true,
            // The backend keeps a customer's primary contact.
            disabled: contact.is_primary,
            hint: (
              <F
                id="component.contacts.removePrimaryHint"
                defaultMessage="Make another contact the customer's primary contact before removing this one."
              />
            ),
            confirm: {
              title: intl.formatMessage({ id: 'component.contacts.removeFromCustomer', defaultMessage: 'Remove from customer' }),
              content: (
                <F
                  id="component.contacts.removeConfirm"
                  defaultMessage="{name} is removed from all projects of {customer}. Someone who isn't a contact of another customer is deleted."
                  values={{ name: <b>{getContactName(contact)}</b>, customer: getCustomerLabel(customer) }}
                />
              ),
            },
            onSelect: handleRemove,
          },
        ]
      : [];
  const footer = isCreate ? createFooter : null;

  // Who this is and where they belong, as the project drawer's header does for projects.
  const header = contact && !isCreate && (
    <Flex className={styles.header} gap={12} align="flex-start">
      <ContactAvatar contact={contact} size="large" />
      <Flex vertical className={styles.headerText}>
        <Typography.Title level={4} className={styles.headerName}>
          {getContactName(contact)}
        </Typography.Title>
        <span className={styles.headerMeta}>
          {customers.slice(0, MAX_HEADER_CUSTOMERS).map((link) => (
            <span key={link.id}>
              {link.type === CUSTOMER_TYPES.ORGANIZATION ? <BankOutlined /> : <UserOutlined />} {getCustomerLabel(link)}
            </span>
          ))}
          {customers.length > MAX_HEADER_CUSTOMERS && (
            <span title={customers.slice(MAX_HEADER_CUSTOMERS).map(getCustomerLabel).join(', ')}>
              +{customers.length - MAX_HEADER_CUSTOMERS}
            </span>
          )}
          {contact.is_primary && (
            <Tag className={styles.customerPrimaryTag} bordered={false}>
              <F id="component.contacts.customerPrimary" defaultMessage="Customer's primary contact" />
            </Tag>
          )}
        </span>
        <span className={styles.headerMeta}>
          {contact.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
          {contact.phone && <PhoneNumberFormat phoneNumber={contact.phone} variant="link" />}
        </span>
      </Flex>
      {actionItems.length > 0 && <ActionsMenu items={actionItems} />}
    </Flex>
  );

  const showSkeleton = !isCreate && !contact && loading;

  return (
    <Drawer
      open={drawer.visible}
      title={title}
      width={720}
      hideCloseIcon
      onOpenChange={(open) => !open && close()}
      toggledAt={drawer.toggledAt}
      footer={footer || null}
      drawerHeaderClassName={styles.drawerHeader}
      drawerBodyClassName={styles.drawerBody}
    >
      {showSkeleton ? (
        <Flex vertical gap={16}>
          <Skeleton active title={false} paragraph={{ rows: 6 }} />
          <Skeleton active title={false} paragraph={{ rows: 3 }} />
        </Flex>
      ) : (
        <Flex vertical gap={16}>
          {header}
          <InfoCard
            variant="blue"
            title={
              <Space>
                <UserOutlined />
                <F id="component.contacts.data" defaultMessage="Contact data" />
              </Space>
            }
          >
            <ContactFields
              form={form}
              values={isCreate ? EMPTY_VALUES : values}
              onFieldSave={isCreate ? undefined : handleFieldSave}
              requiredFields={requiredFields}
              showRole
              roleSuggestions={roleSuggestions}
              disabled={!isCreate && (!customerId || !canEdit)}
            />
            {isCreate && duplicate && <DuplicateElsewhereNotice duplicate={duplicate} className={styles.duplicateAlert} />}
          </InfoCard>

          {!isCreate && (
            <InfoCard
              variant="purple"
              title={
                <Space>
                  <ProjectOutlined />
                  <F id="entity.lead.plural" defaultMessage="Projects" />
                  {projects && projects.length > 0 && <span className={styles.projectCount}>{projects.length}</span>}
                </Space>
              }
            >
              {projects === null || !contact ? (
                <Skeleton active title={false} paragraph={{ rows: 2 }} />
              ) : projects.length > 0 ? (
                <div className={styles.projectList}>
                  {projects.map((lead) => (
                    <ContactProjectRow key={lead.id} lead={lead} contact={contact} onOpen={openProject} />
                  ))}
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={getIntlTranslation(intl, {
                    entity: INTL_ENTITY.LEAD,
                    action: INTL_ACTION.EMPTY,
                  })}
                />
              )}
            </InfoCard>
          )}
        </Flex>
      )}
    </Drawer>
  );
};

export default React.memo(ContactDrawer);
